import { Component, OnInit } from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { SystemSettingsService } from "../../services/system-settings.service";
import {PeriodTypeService} from "../../../setting/services/school/period-type.service";
import {AccountService} from "../../../accounting/services/account.service";
import {JournalService} from "../../../accounting/services/journal.service";
import {CurrencyService} from "../../../setting/services/finance/currency.service";
import {BankAccountService} from "../../../treasury/services/bank-account.service";

@Component({
  selector: 'app-accounting-system-settings',
  templateUrl: './accounting-system-settings.component.html',
  styleUrls: ['./accounting-system-settings.component.scss']
})
export class AccountingSystemSettingsComponent implements OnInit {
  accountingSystemSettingsForm: FormGroup;
  saving = false;
  data: any;
  isLoading = true;
  update = this.translate.instant('Update');
  create = this.translate.instant('Save');

  periodicityTaxes: any[] = [];
  accountingCurrencies: any[] = [];
  defaultInstitutionBankAccounts: any[] = [];
  defaultAssetJournals: any[] = [];
  defaultLibilityJournals: any[] = [];
  customerDiscountAccounts: any[] = [];
  supplierDiscountAccounts: any[] = [];
  incomeProductAccounts: any[] = [];
  chargeProductAccounts: any[] = [];
  periodicityTaxSelected: number | undefined;
  accountingCurrencySelected: number | undefined;
  defaultInstitutionBankAccountSelected: number | undefined;
  defaultAssetJournalSelected: number | undefined;
  defaultLibilityJournalSelected: number | undefined;
  customerDiscountAccountSelected: number | undefined;
  supplierDiscountAccountSelected: number | undefined;
  incomeProductAccountSelected: number | undefined;
  chargeProductAccountSelected: number | undefined;

  constructor(
      private fb: FormBuilder,
      private systemSettingsService: SystemSettingsService,
      private translate: TranslateService,
      private periodTypeService: PeriodTypeService,
      private accountService: AccountService,
      private journalService: JournalService,
      private currencyService: CurrencyService,
      private bankAccountService: BankAccountService,
      private toastr: ToastrService,
      private router: Router
  ) {
    this.accountingSystemSettingsForm = this.fb.group({
      isSingleCompany: [false],
      isBudgetAutomatic: [false],
      isCumulatedZoneQuantity: [false],
      isInventoryZeroing: [false],
      isScaleRange2021: [false],
      isUpdatingInventoryQuantities: [false],
      isPitGrossSalaryPercentage: [false],
      pitGrossSalaryPercentage: [{value: 0, disabled: true},
        [Validators.required, Validators.min(0), Validators.max(100)]],
      professionalExpense: [0, [Validators.min(0)]],
      taxExemptThreshold: [0, [Validators.min(0)]],
      maxProfessionalExpenseAmount: [0, [Validators.min(0)]],
	  contractNoticePeriod: [0, [Validators.min(0)]],
	  trialDuration: [0, [Validators.min(0)]],
	  
     // defaultPriceList: '',
      isSalePriceWithFormula: [false],
      isSalePriceWithPu: [true],

      //Accounting
      periodicityTax: [''],
      deadline: '',
      accountingCurrency: [''],
      defaultInstitutionBankAccount: [''],
      internalPayment: 0,
      defaultAssetJournal: [''],
      prepaidAssetExpenses: '',
      isGenerateEntriesUponInvoiceValidationManually: [false],
      defaultLibilityJournal: [''],
      prepaidLibilityExpenses: '',
      isGenerateEntriesUponInvoiceValidationManuallyLibility: [false],
      discountGainAnticipated: '',
      lossOfAnticipatedDiscount: '',
      customerDiscountAccount: [''],
      supplierDiscountAccount: [''],
      incomeProductAccount: [''],
      chargeProductAccount: [''],

    });


    this.accountingSystemSettingsForm.get('isGenerateEntriesUponInvoiceValidationManually')?.valueChanges.subscribe(value => {
      const percentageControl = this.accountingSystemSettingsForm.get('pitGrossSalaryPercentage');
      if (value) {
        percentageControl?.enable();
        percentageControl?.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
      } else {
        percentageControl?.disable();
        percentageControl?.clearValidators();
      }
      percentageControl?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.loadSystemSettings();
  }

  get fc(): any {
    return this.accountingSystemSettingsForm.controls;
  }
  loadSystemSettings(): void {
    this.systemSettingsService.getCollection().subscribe({
      next: (res: any) => {
        const settings = res['hydra:member'][0];
        this.data = settings;
        this.accountingSystemSettingsForm.patchValue(settings);
        this.findPeriodicityTaxs();
        this.findAccountingCurrencies();
        this.findDefaultInstitutionBankAccounts();
        this.findDefaultAssetJournals();
        this.findDefaultLibilityJournals();
        this.findCustomerDiscountAccounts();
        this.findSupplierDiscountAccounts();
        this.findIncomeProductAccounts();
        this.findChargeProductAccounts();
        // Only need to handle PIT percentage disabled state
        if (!settings.isGenerateEntriesUponInvoiceValidationManually) {
          this.accountingSystemSettingsForm.get('pitGrossSalaryPercentage')?.disable();
        }
        this.accountingSystemSettingsForm.get('periodicityTax')?.setValue(this.periodicityTaxSelected);
        this.accountingSystemSettingsForm.get('accountingCurrency')?.setValue(this.accountingCurrencySelected);
        this.accountingSystemSettingsForm.get('defaultInstitutionBankAccount')?.setValue(this.defaultInstitutionBankAccountSelected);
        this.accountingSystemSettingsForm.get('defaultAssetJournal')?.setValue(this.defaultAssetJournalSelected);
        this.accountingSystemSettingsForm.get('defaultLibilityJournal')?.setValue(this.defaultLibilityJournalSelected);
        this.accountingSystemSettingsForm.get('customerDiscountAccount')?.setValue(this.customerDiscountAccountSelected);
        this.accountingSystemSettingsForm.get('supplierDiscountAccount')?.setValue(this.supplierDiscountAccountSelected);
        this.accountingSystemSettingsForm.get('incomeProductAccount')?.setValue(this.incomeProductAccountSelected);
        this.accountingSystemSettingsForm.get('chargeProductAccount')?.setValue(this.chargeProductAccountSelected);
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error(this.translate.instant('load settings error'), 'Error');
        this.isLoading = false;
      }
    });
  }

  onFormSubmit(): void {
    if (this.accountingSystemSettingsForm.valid && this.data) {
      this.saving = true;

      this.systemSettingsService.put(this.data.id, this.accountingSystemSettingsForm.value).subscribe({
        next: () => {
          this.toastr.success(
              this.translate.instant('settings update success'),
              this.translate.instant('success')
          );
          this.router.navigate(['/dashboard/security']);
          this.loadSystemSettings();
        },
        error: (err) => {
          this.toastr.error(
              this.translate.instant('settings update error'),
              this.translate.instant('error')
          );
          console.error(err);
          this.saving = false;
        }
      });
    }
  }

  findPeriodicityTaxs(): any{
    this.periodTypeService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.periodicityTaxes = data['hydra:member'];
          this.periodicityTaxes = this.periodicityTaxes.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
  findAccountingCurrencies(): any{
    this.currencyService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.accountingCurrencies = data['hydra:member'];
          this.accountingCurrencies = this.accountingCurrencies.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
  findDefaultInstitutionBankAccounts(): any{
    this.bankAccountService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.defaultInstitutionBankAccountSelected = data['hydra:member'];
          this.defaultInstitutionBankAccounts = this.defaultInstitutionBankAccounts.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
  findDefaultAssetJournals(): any{
    this.journalService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.defaultAssetJournals = data['hydra:member'];
          this.defaultAssetJournals = this.defaultAssetJournals.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
  findDefaultLibilityJournals(): any{
    this.journalService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.defaultLibilityJournals = data['hydra:member'];
          this.defaultLibilityJournals = this.defaultLibilityJournals.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
  findCustomerDiscountAccounts(): any{
    this.accountService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.customerDiscountAccounts = data['hydra:member'];
          this.customerDiscountAccounts = this.customerDiscountAccounts.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }

  findSupplierDiscountAccounts(): any{
    this.accountService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.supplierDiscountAccounts = data['hydra:member'];
          this.supplierDiscountAccounts = this.supplierDiscountAccounts.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
  findIncomeProductAccounts(): any{
    this.accountService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.incomeProductAccounts = data['hydra:member'];
          this.incomeProductAccounts = this.incomeProductAccounts.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
  findChargeProductAccounts(): any{
    this.accountService.getCollection().subscribe((data: any) => {
          console.log(data);
          this.chargeProductAccounts = data['hydra:member'];
          this.chargeProductAccounts = this.chargeProductAccounts.map((v) => {
            v.id = v['@id'];
            return v;
          });
        },
        error => console.log(error)
    );
  }
}